import { Checkbox, InputNumber, Modal, Space, Typography } from 'antd'
import { useState } from 'react'
import {
  buildLaunchOverrides,
  EvalRunPreset,
  formatSelectionSummary,
  launchFormFromPreset,
  PresetLaunchForm,
} from './presetsAdminModel'
import { useLaunchPresetMutation } from './presetsAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
  preset: EvalRunPreset
}

/**
 * Launch a preset. The selection is fixed (and re-resolved server-side at
 * launch); only inference dimensions can be overridden, and only for this
 * launch — the stored preset is untouched. Pre-filled with the preset's own
 * dimensions, so confirming without edits is the plain-launch path.
 */
const LaunchPresetModal = ({ open, onClose, preset }: Props) => {
  const [form, setForm] = useState<PresetLaunchForm>(() =>
    launchFormFromPreset(preset),
  )
  const set = <K extends keyof PresetLaunchForm>(
    key: K,
    value: PresetLaunchForm[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }))

  const launchMutation = useLaunchPresetMutation()

  const handleClose = () => {
    setForm(launchFormFromPreset(preset))
    onClose()
  }

  const handleOk = () => {
    launchMutation.mutate(
      { presetId: preset.preset_id, overrides: buildLaunchOverrides(form) },
      { onSuccess: handleClose },
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      okText="Launch run"
      title={`Launch "${preset.name}"`}
      confirmLoading={launchMutation.isLoading}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Selection: {formatSelectionSummary(preset)}. The selection is
        re-resolved against the current cases at launch. Overrides below apply
        to this launch only.
      </Typography.Paragraph>
      <Space size="large" align="center">
        <Checkbox
          checked={form.includeMutants}
          onChange={(e) => set('includeMutants', e.target.checked)}
        >
          Include mutants
        </Checkbox>
        <span>
          Trials per case{' '}
          <InputNumber
            min={1}
            value={form.trials}
            onChange={(value) => set('trials', value ?? 1)}
          />
        </span>
      </Space>
    </Modal>
  )
}

export default LaunchPresetModal
